import type { Camera } from '@babylonjs/core/Cameras/camera';
import '@babylonjs/core/Culling/ray';
import { PointerEventTypes } from '@babylonjs/core/Events/pointerEvents';
import { Color3, Color4 } from '@babylonjs/core/Maths/math.color';
import { Quaternion } from '@babylonjs/core/Maths/math.vector';
import { StandardMaterial } from '@babylonjs/core/Materials/standardMaterial';
import { DynamicTexture } from '@babylonjs/core/Materials/Textures/dynamicTexture';
import { MeshBuilder } from '@babylonjs/core/Meshes/meshBuilder';
import type { Scene } from '@babylonjs/core/scene';
import type { ShellState, ShellStore } from '../ui/shell';
import { createFloatingPageInput } from '../input/floatingPageInput';
import { AdvancedDynamicTexture } from '@babylonjs/gui/2D/advancedDynamicTexture';
import { Button } from '@babylonjs/gui/2D/controls/button';
import { CAPTURE_GAP_MS, captureDue } from './pageCapture';

export const PAGE_LAYER = 0x10000000;
export const showsFloatingPage = (state: Pick<ShellState, 'inXr' | 'page'>) =>
  state.inXr && state.page != null;
export const canMoveBoard = (state: Pick<ShellState, 'inXr' | 'page'>, fighting: boolean) =>
  state.inXr && state.page == null && !fighting;

/** Upright, world-fixed screen, placed afresh only when opening a different page. */
export function pagePose(camera: Camera) {
  camera.computeWorldMatrix();
  const direction = camera.getForwardRay().direction.clone();
  direction.y = 0;
  if (direction.lengthSquared() < 0.001) direction.set(0, 0, 1);
  direction.normalize();
  return {
    position: camera.globalPosition.add(direction.scale(1.6)),
    // Babylon planes face local -Z. Keep local +X on the viewer's right; a look-at
    // rotation toward the page exposes its back and mirrors the page and pointer targets.
    rotation: Quaternion.RotationYawPitchRoll(Math.atan2(direction.x, direction.z), 0, 0),
  };
}

/** Reuses the actual React page, including forms/editor, instead of a second reduced VR menu. */
export function createFloatingPages(
  scene: Scene,
  camera: Camera,
  shell: ShellStore,
  exit: () => Promise<void>,
) {
  const plane = MeshBuilder.CreatePlane('floatingPage', { width: 1.8, height: 1 }, scene);
  plane.layerMask = PAGE_LAYER;
  plane.isPickable = true;
  plane.setEnabled(false);
  const material = new StandardMaterial('floatingPageMaterial', scene);
  material.disableLighting = true;
  material.backFaceCulling = false;
  material.diffuseColor = Color3.Black();
  material.specularColor = Color3.Black();
  plane.material = material;
  // Native XR exit stays usable even before the page raster loads (or if it fails).
  const exitMesh = MeshBuilder.CreatePlane('floatingPageVr', { size: 1 }, scene);
  exitMesh.parent = plane;
  exitMesh.layerMask = PAGE_LAYER;
  exitMesh.position.z = -0.002;
  const exitTexture = AdvancedDynamicTexture.CreateForMesh(exitMesh, 128, 128);
  const exitButton = Button.CreateSimpleButton('page-vr', 'VR');
  exitButton.color = '#ffe34a';
  exitButton.background = '#17202c';
  exitButton.fontSize = 44;
  exitButton.onPointerClickObservable.add(() => {
    void exit().catch(() => {
      exitButton.textBlock!.text = 'Retry';
    });
  });
  exitTexture.addControl(exitButton);
  let texture: DynamicTexture | null = null;
  let root: HTMLElement | null = null;
  let input: ReturnType<typeof createFloatingPageInput> | null = null;
  let observer: MutationObserver | null = null;
  let key: string | null = null;
  let dirty = true,
    busy = false,
    disposed = false,
    nextCapture = 0;
  /** When the page last changed, since when the headset has shown it out of date, a trigger on it. */
  let changedAt = -Infinity,
    dirtySince = -Infinity,
    pressed = false;
  let revision = 0;
  const invalidate = () => {
    const now = performance.now();
    if (!dirty) dirtySince = now;
    dirty = true;
    changedAt = now;
  };
  const end = () => {
    revision++;
    observer?.disconnect();
    observer = null;
    root?.removeEventListener('scroll', invalidate, true);
    root?.removeEventListener('input', invalidate, true);
    input?.dispose();
    input = null;
    root = null;
    plane.setEnabled(false);
    key = null;
  };
  const capture = async () => {
    if (!root || busy) return;
    const element = root,
      generation = revision;
    busy = true;
    dirty = false;
    try {
      const { default: html2canvas } = await import('html2canvas');
      const canvas = await html2canvas(element, {
        backgroundColor: '#0c1020',
        scale: 1,
        logging: false,
        useCORS: true,
        width: element.clientWidth,
        height: element.clientHeight,
        ignoreElements: (e) => e.tagName === 'IFRAME',
        onclone: (document) => {
          // Keep the native exit's flex slot. Removing the DOM button from the clone
          // shrinks the right-aligned row and slides the radio underneath the XR exit.
          document.querySelectorAll<HTMLElement>('.vr-toggle').forEach((button) => {
            button.style.visibility = 'hidden';
          });
        },
      });
      if (disposed || generation !== revision) return;
      if (
        !texture ||
        texture.getSize().width !== canvas.width ||
        texture.getSize().height !== canvas.height
      ) {
        texture?.dispose();
        texture = new DynamicTexture(
          'floatingPageTexture',
          { width: canvas.width, height: canvas.height },
          scene,
          false,
        );
        material.emissiveTexture = texture;
      }
      texture.getContext().drawImage(canvas, 0, 0);
      texture.update();
      plane.isPickable = true;
      plane.scaling.y = (1.8 * canvas.height) / canvas.width;
    } catch (error) {
      // Keep a readable exit even if a third-party image fails to rasterize.
      console.warn('Floating page capture failed', error);
    } finally {
      busy = false;
      nextCapture = performance.now() + CAPTURE_GAP_MS;
    }
  };
  const pointer = scene.onPointerObservable.add(
    (event) => {
      if (event.type === PointerEventTypes.POINTERUP) pressed = false;
      if (!input || event.pickInfo?.pickedMesh !== plane) return;
      if (event.type === PointerEventTypes.POINTERDOWN) pressed = true;
      const uv = event.pickInfo.getTextureCoordinates();
      if (!uv || !root) return;
      const rect = root.getBoundingClientRect();
      const kind =
        event.type === PointerEventTypes.POINTERDOWN
          ? 'down'
          : event.type === PointerEventTypes.POINTERUP
            ? 'up'
            : 'move';
      input.point(kind, rect.left + uv.x * rect.width, rect.top + (1 - uv.y) * rect.height);
    },
    PointerEventTypes.POINTERDOWN | PointerEventTypes.POINTERUP | PointerEventTypes.POINTERMOVE,
  );
  return {
    plane,
    update() {
      const state = shell.getSnapshot();
      if (!showsFloatingPage(state)) {
        if (key !== null) end();
        return;
      }
      if (!shell.screenElement) return;
      if (key !== state.page || root !== shell.screenElement) {
        end();
        root = shell.screenElement;
        key = state.page!;
        const pose = pagePose(camera);
        plane.position.copyFrom(pose.position);
        plane.rotationQuaternion = pose.rotation;
        plane.isPickable = false;
        if (texture) {
          const context = texture.getContext();
          context.fillStyle = '#0c1020';
          context.fillRect(0, 0, texture.getSize().width, texture.getSize().height);
          context.fillStyle = 'white';
          context.font = '24px sans-serif';
          context.fillText('Opening page…', 28, 60);
          texture.update();
        }
        input = createFloatingPageInput(root, invalidate);
        observer = new MutationObserver(invalidate);
        observer.observe(root, {
          childList: true,
          subtree: true,
          attributes: true,
          characterData: true,
        });
        root.addEventListener('scroll', invalidate, true);
        root.addEventListener('input', invalidate, true);
        plane.setEnabled(true);
        dirty = true;
        nextCapture = 0;
        // A page just opened is drawn at once.
        changedAt = dirtySince = -Infinity;
      }
      const width = root.clientWidth,
        height = root.clientHeight;
      plane.scaling.y = (1.8 * height) / width;
      const buttonRect = root.querySelector('.vr-toggle')?.getBoundingClientRect();
      const w = buttonRect?.width || 48,
        h = buttonRect?.height || 48;
      exitMesh.position.x =
        1.8 * ((buttonRect ? buttonRect.left + w / 2 : width - 40) / width - 0.5);
      exitMesh.position.y = 0.5 - (buttonRect ? buttonRect.top + h / 2 : 40) / height;
      exitMesh.scaling.set((1.8 * w) / width, h / height, 1);
      const clock = { dirty, busy, notBefore: nextCapture, changedAt, dirtySince, pressed };
      if (captureDue(clock, performance.now())) void capture();
    },
    scroll(delta: number) {
      input?.scroll(delta);
    },
    dispose() {
      disposed = true;
      end();
      scene.onPointerObservable.remove(pointer);
      texture?.dispose();
      exitTexture.dispose();
      exitMesh.dispose(false, true);
      plane.dispose();
      material.dispose();
    },
  };
}

/** Camera filtering also hides unparented sky/arena effects while a page is open. */
export function showOnlyPage(camera: Camera, enabled: boolean, normalMask: number) {
  camera.layerMask = enabled ? PAGE_LAYER : normalMask;
  for (const eye of camera.rigCameras) eye.layerMask = camera.layerMask;
}
export const EMPTY_PAGE_SPACE = new Color4(0.015, 0.02, 0.035, 1);
